import { SortDirection } from '@recurrency/core-api-schema/dist/common/enums';
import { InventoryDashboardGroupBy } from '@recurrency/core-api-schema/dist/inventory/getInventoryDashboard';

import {
  InventoryDashboardDetailsHashState,
  InventoryExplorerCompareFromOptions,
  InventoryExplorerCompareToOptions,
} from 'types/hash-state';

export interface DrillDownChartConfigItem {
  name: string;
  title: string;
  description: string;
  hashStateConfig: InventoryDashboardDetailsHashState;
}

export const DrillDownChartConfig: Record<string, DrillDownChartConfigItem> = {
  inventory: {
    name: 'inventory' as const,
    title: 'Inventory History' as const,
    description:
      'Shows how your inventory value has changed over time, based on item transaction costs. Monthly values are averaged from the start and end of each month to reflect how long inventory was held.' as const,
    hashStateConfig: {
      chartSource: 'inventory',
      displayUnit: 'value',
      changeValue: 'percent',
      page: 1,
      columnGroups: ['inventory', 'sales', 'turns'],
      sortBy: [['compareFrom.inventoryValue', SortDirection.Desc]],
      groupBy: InventoryDashboardGroupBy.Overview,
      compareFrom: InventoryExplorerCompareFromOptions.LastThreeMonths,
      compareTo: InventoryExplorerCompareToOptions.PriorThreeMonths,
    },
  },
  sales: {
    name: 'sales' as const,
    title: 'Sales History' as const,
    description: 'Total quantity or value of items invoiced each month, based on recorded sales transactions.' as const,
    hashStateConfig: {
      chartSource: 'sales',
      displayUnit: 'value',
      changeValue: 'percent',
      page: 1,
      columnGroups: ['sales', 'inventory', 'turns'],
      sortBy: [['compareFrom.sales', SortDirection.Desc]],
      groupBy: InventoryDashboardGroupBy.Overview,
      compareFrom: InventoryExplorerCompareFromOptions.LastThreeMonths,
      compareTo: InventoryExplorerCompareToOptions.PriorThreeMonths,
    },
  },
  backorders: {
    name: 'backorders' as const,
    title: 'Backorder History' as const,
    description:
      'Tracks backordered quantities over time, calculated using historical average cost to show the value of unfulfilled orders.' as const,
    hashStateConfig: {
      chartSource: 'backorders',
      displayUnit: 'value',
      changeValue: 'percent',
      page: 1,
      columnGroups: ['backorders', 'inventory', 'fillRate'],
      sortBy: [['compareFrom.backorderValue', SortDirection.Desc]],
      groupBy: InventoryDashboardGroupBy.Overview,
      compareFrom: InventoryExplorerCompareFromOptions.LastThreeMonths,
      compareTo: InventoryExplorerCompareToOptions.PriorThreeMonths,
    },
  },
  turns: {
    name: 'turns' as const,
    title: 'Annualized Turns' as const,
    description:
      'Calculated each month by dividing cost of goods sold by average inventory and then annualized.' as const,
    hashStateConfig: {
      chartSource: 'turns',
      displayUnit: 'value',
      changeValue: 'percent',
      page: 1,
      columnGroups: ['turns', 'inventory', 'cost'],
      sortBy: [['compareFrom.turns', SortDirection.Desc]],
      groupBy: InventoryDashboardGroupBy.Overview,
      compareFrom: InventoryExplorerCompareFromOptions.LastThreeMonths,
      compareTo: InventoryExplorerCompareToOptions.PriorThreeMonths,
    },
  },
  grossMarginROI: {
    name: 'grossMarginROI' as const,
    title: 'Annualized GMROI' as const,
    description:
      'Measures monthly GMROI, calculated from gross margin and average inventory value, then annualized to show return on inventory investment over a year.' as const,
    hashStateConfig: {
      chartSource: 'grossMarginROI',
      displayUnit: 'value',
      changeValue: 'percent',
      page: 1,
      columnGroups: ['grossMarginROI', 'sales', 'cost', 'inventory'],
      sortBy: [['compareFrom.grossMarginROI', SortDirection.Desc]],
      groupBy: InventoryDashboardGroupBy.Overview,
      compareFrom: InventoryExplorerCompareFromOptions.LastThreeMonths,
      compareTo: InventoryExplorerCompareToOptions.PriorThreeMonths,
    },
  },
  cost: {
    name: 'COGS' as const,
    title: 'Cost of Goods Sold' as const,
    description: '' as const,
    hashStateConfig: {
      chartSource: 'cost',
      displayUnit: 'value',
      changeValue: 'percent',
      page: 1,
      columnGroups: ['cost', 'sales', 'grossMarginROI'],
      sortBy: [['compareFrom.cost', SortDirection.Desc]],
      groupBy: InventoryDashboardGroupBy.Overview,
      compareFrom: InventoryExplorerCompareFromOptions.LastThreeMonths,
      compareTo: InventoryExplorerCompareToOptions.PriorThreeMonths,
    },
  },
  fillRate: {
    name: 'fillRate' as const,
    title: 'Fill Rate' as const,
    description:
      'Percentage of items on orders that shipped complete and on time each month, excluding cancellations. Completeness is per item; on time means shipped within the configured grace period of the order’s requested date.' as const,
    hashStateConfig: {
      chartSource: 'fillRate',
      displayUnit: 'value',
      changeValue: 'percent',
      page: 1,
      columnGroups: ['fillRate', 'inventory', 'backorders'],
      sortBy: [['compareFrom.fillRate', SortDirection.Desc]],
      groupBy: InventoryDashboardGroupBy.Overview,
      compareFrom: InventoryExplorerCompareFromOptions.LastThreeMonths,
      compareTo: InventoryExplorerCompareToOptions.PriorThreeMonths,
    },
  },
};
